import { ChangeSet, Text } from "@codemirror/state";
import { Data, type DateTime, Either, Schema } from "effect";
import {
  ConversionFindingSchema,
  EnrichmentReadinessSchema,
  RailFinding,
  READINESS_NOT_COMPUTED,
} from "./enrichment-readiness.ts";
import { FixScopeRuleId } from "./finding.ts";
import { FindingSeverity, FindingSummary } from "./finding-summary.ts";
import { AnnotationId, ConnectionId, DocumentId } from "./ids.ts";

// ─── Wire-format JSON shape for @codemirror/state ChangeSet ───────────────────
//
// ChangeSet.toJSON() returns an array of numbers and [number, ...string[]] tuples.
// @codemirror/state is imported for the pure rebase and apply helpers (U3).
// The SCHEMA (ChangeSetJson) remains structural — decode does not construct a ChangeSet.
// The dep is DOM-free and Bun-safe; the workspace-root override pins exactly one resolution
// (enforced by packages/mcp/test/codemirror-singleton.test.ts, ADR-0043).
// NOTE: packages/core previously kept @codemirror/state out of core (option b from the U2
// plan). U3 intentionally takes the dep for rebase/apply while keeping the schema
// representation agnostic — the ChangeSetJson brand still lives here and the decode
// boundary still validates shape without constructing a ChangeSet.

// The raw JSON shape of a ChangeSet (ChangeSet.toJSON() output). An array of
// entries — either retained-length integers or delete+insert tuples of the form
// [number, ...string[]] (number = deleted-chars count, strings = inserted spans).
// The brand "ChangeSetJson" makes it unconfusable with an arbitrary array.
export const ChangeSetJson = Schema.Array(
  Schema.Union(Schema.Number, Schema.Array(Schema.Union(Schema.Number, Schema.String))),
).pipe(
  Schema.filter(
    (arr) => {
      for (const entry of arr) {
        if (typeof entry === "number") {
          // retained segment — must be a non-negative integer
          if (!Number.isInteger(entry) || entry < 0)
            return "ChangeSetJson: retained lengths must be non-negative integers";
          continue;
        }
        if (!Array.isArray(entry)) return "ChangeSetJson: entry must be number or array";
        if (entry.length < 1) return "ChangeSetJson: tuple entry must be non-empty";
        const first = entry[0];
        if (typeof first !== "number" || !Number.isInteger(first) || first < 0)
          return "ChangeSetJson: first element of tuple must be a non-negative integer";
        for (let i = 1; i < entry.length; i++) {
          if (typeof entry[i] !== "string")
            return "ChangeSetJson: tuple entries after index 0 must be strings";
        }
      }
      return true;
    },
    { message: () => "Invalid ChangeSetJson shape" },
  ),
  Schema.brand("ChangeSetJson"),
);
export type ChangeSetJson = typeof ChangeSetJson.Type;

// Decodes a raw unknown value into a branded ChangeSetJson or surfaces MalformedChangeSet.
// Usage: at the decode boundary (WS frame, audit event replay, DraftStore read).
export const decodeChangeSetJson = (
  raw: unknown,
): Either.Either<ChangeSetJson, MalformedChangeSet> => {
  const result = Schema.decodeUnknownEither(ChangeSetJson)(raw);
  return Either.mapLeft(result, (parseError) => new MalformedChangeSet({ cause: parseError }));
};

// ─── Version types ─────────────────────────────────────────────────────────────
//
// CollabVersion is the branded form of docVersion used in the collab model.
// collabVersion === docVersion is a type identity (one brand, not two) per ADR-0043.
//
// NOTE: audit.ts exports an unbranded `DocVersion` (Schema.Int.pipe(nonNegative()))
// for backward compat with legacy events. New code uses CollabVersion. We do NOT
// re-export `DocVersion` here to avoid a name collision in index.ts.

export const CollabVersion = Schema.Int.pipe(Schema.nonNegative(), Schema.brand("CollabVersion"));
export type CollabVersion = typeof CollabVersion.Type;

// ─── Draft ids and identities ──────────────────────────────────────────────────

export const DraftId = Schema.String.pipe(Schema.brand("DraftId"));
export type DraftId = typeof DraftId.Type;

// The viewer identity: a per-connection display identity derived from presence.
// We reference the ConnectionId brand already defined in ids.ts.
export const ViewerIdentity = Schema.TaggedStruct("viewer", {
  connectionId: ConnectionId,
  displayName: Schema.String,
  color: Schema.String,
});
export type ViewerIdentity = typeof ViewerIdentity.Type;

// The agent's synthetic identity — a stable session-scoped name and color, tagged
// distinctly so gate-2 logic can distinguish human vs agent authors.
export const AgentIdentity = Schema.TaggedStruct("agent", {
  // Stable synthetic ConnectionId minted per session (registered via presence-hub).
  connectionId: ConnectionId,
  displayName: Schema.String,
  color: Schema.String,
});
export type AgentIdentity = typeof AgentIdentity.Type;

// The union — every editor (human viewer or headless agent) is one of these.
export const EditorIdentity = Schema.Union(ViewerIdentity, AgentIdentity);
export type EditorIdentity = typeof EditorIdentity.Type;

// Smart constructors
export const viewerIdentity = (
  connectionId: ConnectionId,
  displayName: string,
  color: string,
): EditorIdentity => ViewerIdentity.make({ connectionId, displayName, color });

export const agentIdentity = (
  connectionId: ConnectionId,
  displayName: string,
  color: string,
): EditorIdentity => AgentIdentity.make({ connectionId, displayName, color });

// The canonical agent editor identity (ADR-0044). Every MCP-opened draft is stamped agent-owned with
// this identity, so reviewers see the agent's draft labelled consistently and the land gate always
// routes it through the human accept/reject path (gate 2) — a stateless MCP client (the harness agent
// OR a directly attached claude-code) never self-lands. The reserved hue distinguishes the agent from
// the human viewer palette without being human-attributable.
export const AGENT_DISPLAY_NAME = "Curriculum Agent";
export const AGENT_COLOR = "hsl(280, 65%, 45%)";

// Mint a fresh agent identity. The `connectionId` is a per-draft synthetic key: with the stateless
// surface it is not a presence key (presence is a turn-lifecycle concern now), only an attribution
// handle, so a fresh id per open is fine — draft ownership and the gate are keyed by `draftId`.
export const mintAgentIdentity = (): EditorIdentity =>
  agentIdentity(ConnectionId.make(crypto.randomUUID()), AGENT_DISPLAY_NAME, AGENT_COLOR);

// The reference-guide/language-pack stores' commit identity (U2, generalized document versioning;
// ADR-0109). `ReferenceGuideStoreService.write`/`LanguagePackStoreService.write` keep their
// pre-existing `(path, content, expectedRevision)` signature — no `EditorIdentity` is threaded in
// from the REST layer — so the pg adapters cannot attribute a `CommitLanded` event to the actual
// caller. Tagged `viewer`, not `agent`: both document classes have no agent write surface at all
// (ADR-0097 Decision 4, ADR-0099) and `AgentIdentity`'s reserved hue exists so gate-2 logic can tell
// a human edit from an agent one — mislabelling a human studio edit as the agent would be actively
// wrong, not merely imprecise. A distinct display name/colour from `mintAgentIdentity()`'s so the
// History tab never shows these edits as the Curriculum Agent's.
export const SYSTEM_EDITOR_DISPLAY_NAME = "Studio Editor";
export const SYSTEM_EDITOR_COLOR = "hsl(210, 15%, 45%)";

export const mintSystemIdentity = (): EditorIdentity =>
  viewerIdentity(
    ConnectionId.make(crypto.randomUUID()),
    SYSTEM_EDITOR_DISPLAY_NAME,
    SYSTEM_EDITOR_COLOR,
  );

// ─── OffsetRegion (anchor offsets) ─────────────────────────────────────────────
//
// A `{ from, to }` byte-offset span — used for conflict regions and annotation
// anchors. Always head-relative (D12: offsets are stored output, never op input).
// Named OffsetRegion to avoid collision with the existing Region in locator.ts
// (which is a text-anchor Locator variant, unrelated to byte offsets).

export const OffsetRegion = Schema.Struct({
  from: Schema.Int.pipe(Schema.nonNegative()),
  to: Schema.Int.pipe(Schema.nonNegative()),
});
export type OffsetRegion = typeof OffsetRegion.Type;

// ─── Draft status machine ──────────────────────────────────────────────────────
//
// Closed status set: open → submitted → landed | discarded
//                   submitted → stale  (on rebase conflict while submitted)
//                   open | submitted | stale → discarded  (D11)
// An open draft with a rebase conflict stays open with conflictRegions populated —
// conflict is data, not a status transition.

export const DraftStatus = Schema.Literal("open", "submitted", "landed", "discarded", "stale");
export type DraftStatus = typeof DraftStatus.Type;

// The rail's `[Fix]`/`[Fix all]` structured dispatch target (CON-1315, Decision 8) — also the shape
// stamped onto the `Draft` it opens (see `fixScope` below), so the SAME `{ ruleId, headingPath? }`
// that told the engine what to fix is what tells a reloaded rail which draft answers which row,
// without re-deriving it from `reason`'s free text. `headingPath` absent means whole-document scope
// (`[Fix all]`); present means one row's own heading (`[Fix]`).
// `ruleId` is `FixScopeRuleId` (CON-1334), not `FindingId` alone — a closed union admitting the two
// conversion-family ids `EXERCISE_MISSING_GENERATED_FIELD`/`CONVERSION_MISSING_AUTHORED_CONTENT`,
// so an unknown string still fails to decode.
export const FixScope = Schema.Struct({
  ruleId: FixScopeRuleId,
  headingPath: Schema.optionalWith(Schema.Array(Schema.String), { exact: true }),
});
export type FixScope = typeof FixScope.Type;

// ─── Draft domain type ─────────────────────────────────────────────────────────

export const Draft = Schema.Struct({
  draftId: DraftId,
  docId: DocumentId,
  owner: EditorIdentity,
  // The collabVersion this draft was forked from.
  baseVersion: CollabVersion,
  // Composite ChangeSet (all edits applied since fork), encoded as ChangeSet.toJSON().
  changes: ChangeSetJson,
  // Per-draft monotonic update counter — used for optimistic concurrency on fold.
  draftVersion: Schema.Int.pipe(Schema.nonNegative()),
  // Server-stamped wall time of the draft's last activity: set at `create`, bumped on every
  // `foldUpdate` (an edit). NOT bumped by a rebase-over-land (that is not owner activity) nor by a
  // status transition. Rides the wire on `openDrafts` and `draft-state` frames so the client can
  // compute a server-relative idle basis (U7 idle-hide) — always paired with a per-frame `serverNow`
  // so the age is measured server-to-server, never against a (possibly skewed) client wall clock.
  lastActivityAt: Schema.DateTimeUtc,
  status: DraftStatus,
  // Populated when a rebase conflict hits an open draft (status stays open).
  conflictRegions: Schema.optional(Schema.Array(OffsetRegion)),
  // Free-text reason for the draft (submission note, rejection reason, etc.).
  reason: Schema.optional(Schema.String),
  // The display name of the human who caused this agent-owned draft to be opened (not the editor
  // identity itself, which stays `mintAgentIdentity()`'s "Curriculum Agent" — this is a second,
  // separate axis). Stamped at `create` from `CurrentViewer` for the REST-triggered expand run only.
  // Absent for a human-owned draft, and for every MCP `draft-open` draft — the conversational path has
  // no verified server-side source for this field (see ADR-0044 amendment). Never touched by
  // `submitDraft`/`landDraft`/any later transition.
  instructedByDisplayName: Schema.optional(Schema.String),
  // ADR-0102 (U2): the per-run task backing this open agent-authored expand draft, so a reconcile
  // pass can reattach to a running/finished task after a studio crash or restart instead of relying
  // on an in-process sidecar connection. `taskRunId` is stamped once, right after `create`, before
  // the task is launched (it is also the `TaskLaunchSpec.taskRunId`/ECS `clientToken` used for that
  // launch); `taskArn` is stamped once, right after `TaskLauncherService.launch` returns its
  // `TaskHandle`. Both plain strings (see `packages/ports/src/task-launcher.ts`'s `TaskHandle`), not
  // branded ids — `taskArn` in particular is adapter-opaque (an ECS task ARN, a Docker container
  // id/name). Absent for any draft not backed by a per-run task, and for every draft created before
  // this change.
  taskRunId: Schema.optional(Schema.String),
  taskArn: Schema.optional(Schema.String),
  // Mandatory-at-submit change description: a one-line explanation of what this draft changes and
  // why, collected at the submit boundary (`draft-submit` / `draft-complete`) and enforced non-empty
  // there. Optional on the schema so an OPEN draft (and historical/migrated drafts) decode without
  // one; it is populated only once the draft is submitted, and rides `draft-state` so the gate-2
  // reviewer sees it before deciding, and is folded into `draft.landed` so the audit log keeps it.
  description: Schema.optional(Schema.String),
  // The finding-id(s) this draft answers (ADR-0049 D3): the annotations its land RESOLVES. Named
  // explicitly by the submitting agent/human at the submit boundary — never inferred from
  // anchor-overlap. Empty for a routine edit that answers no finding (a human self-land naming none
  // resolves nothing). `optionalWith default []` so an OPEN or historical draft decodes to an empty
  // list rather than failing; it is stamped only at submit and folded into `draft.landed`, where the
  // land seam emits one `annotation.resolved.v2` per still-open named finding.
  resolves: Schema.optionalWith(Schema.Array(AnnotationId), { default: () => [] }),
  // Which `[Fix]`/`[Fix all]` click opened this draft, if any (CON-1315 QA#1/#2 — reload survival for
  // both the deterministic REST fix and the agentic fix-dispatch turn). Stamped once at `create` for
  // the deterministic path (`fix-route.ts`, `ruleId` and `headingPath` both known up front) and via
  // `DraftStore.setFixScope` once the agentic path's `draft-open` tool call reports its `draftId`
  // (`agent-turn-route.ts`'s `onDraftOpened`, the turn's own `fixScope` known from dispatch). Absent
  // for a human-opened or conversational-agent draft. Rides `draft-state` on the `Draft` itself, so a
  // reload just re-derives `fixState` from `openDraftIds ∪ submittedDraftIds` and each draft's own
  // `fixScope` — no separate lookup, and no dependence on `reason`'s unstructured text.
  fixScope: Schema.optional(FixScope),
});
export type Draft = typeof Draft.Type;

// ─── Draft status transition functions ────────────────────────────────────────
//
// Each function encodes exactly one legal transition. Illegal transitions are
// surfaced as tagged errors rather than a generic setter, making illegal states
// unrepresentable at the call site.

// open → submitted. `description` is the mandatory-at-submit change explanation (enforced non-empty
// at the op boundary, not here — this pure transition just carries whatever it is handed onto the
// submitted record). When present it is stamped on the draft so it rides `draft-state` to reviewers
// and is available to fold into `draft.landed`. `resolves` is the finding-id(s) the submitter names
// this draft as answering (ADR-0049 D3); stamped when supplied so the land seam can resolve them.
//
// `now` refreshes `lastActivityAt`, and a submit is the only status change that does. It is the owner's
// own deliberate act, and the draft it produces STAYS in the live set — so the abandoned-draft reaper
// reads its clock. Without the bump a draft's idle age is measured from its last keystroke, which makes
// the submitted state the worst-treated one on the board: the author finishes their edit, submits, and
// the draft is a candidate for reclamation from the moment the TTL clears the editing they already
// stopped doing — reclaimed while it is waiting on a REVIEWER, which is not abandonment. It also makes
// the rail's "idle for N days" true of an unreviewed submission, which is the signal a reviewer acts on.
// The terminal transitions (`landed`, `rejected`, `discarded`, `stale`) leave the live set, so nothing
// reads their clock and none of them takes an argument for it.
export const submitDraft = (
  draft: Draft,
  now: DateTime.Utc,
  description?: string,
  resolves?: readonly AnnotationId[],
): Either.Either<Draft, DraftNotInOpenStatus> => {
  if (draft.status !== "open") {
    return Either.left(new DraftNotInOpenStatus({ draftId: draft.draftId, actual: draft.status }));
  }
  return Either.right({
    ...draft,
    status: "submitted" as const,
    lastActivityAt: now,
    ...(description !== undefined ? { description } : {}),
    ...(resolves !== undefined ? { resolves } : {}),
  });
};

// submitted → landed (only the server gate may call this after a land)
export const landDraft = (draft: Draft): Either.Either<Draft, DraftNotSubmitted> => {
  if (draft.status !== "submitted") {
    return Either.left(new DraftNotSubmitted({ draftId: draft.draftId, actual: draft.status }));
  }
  return Either.right({ ...draft, status: "landed" as const });
};

// submitted → stale (rebase conflict while the draft is in submitted state)
export const markStale = (draft: Draft): Either.Either<Draft, DraftNotSubmitted> => {
  if (draft.status !== "submitted") {
    return Either.left(new DraftNotSubmitted({ draftId: draft.draftId, actual: draft.status }));
  }
  return Either.right({ ...draft, status: "stale" as const, conflictRegions: undefined });
};

// open | submitted | stale → discarded  (D11: any human may discard any draft)
// landed is not discardable (already canonical).
export const discardDraft = (draft: Draft): Either.Either<Draft, DraftAlreadyTerminal> => {
  if (draft.status === "landed" || draft.status === "discarded") {
    return Either.left(new DraftAlreadyTerminal({ draftId: draft.draftId, status: draft.status }));
  }
  return Either.right({ ...draft, status: "discarded" as const });
};

// Populate conflictRegions on an open draft (rebase conflict while owner still editing).
// Status stays "open" — conflict is data, not a status.
export const setConflictRegions = (
  draft: Draft,
  regions: readonly OffsetRegion[],
): Either.Either<Draft, DraftNotInOpenStatus> => {
  if (draft.status !== "open") {
    return Either.left(new DraftNotInOpenStatus({ draftId: draft.draftId, actual: draft.status }));
  }
  return Either.right({ ...draft, conflictRegions: regions });
};

// ─── Tagged errors ─────────────────────────────────────────────────────────────

// A landed change arrived at the wrong version (base mismatch on the server).
export class VersionConflict extends Data.TaggedError("VersionConflict")<{
  readonly expected: CollabVersion;
  readonly actual: CollabVersion;
}> {}

// A draft with the requested id does not exist in the DraftStore.
export class DraftNotFound extends Data.TaggedError("DraftNotFound")<{
  readonly draftId: DraftId;
}> {}

// The document's `generationStatus` is "pending" (KTD1/KTD3): its `generating`-origin genesis has
// no terminal event yet, so there is no real content to open a draft against, land a commit into,
// or annotate. Not retargetable like a stale span — the caller just has to wait for the run to
// resolve (`document.generation-completed`/`-failed`), so this is deliberately not added to
// `RECOVERABLE_KINDS` (op-error.ts).
export class DocumentGenerating extends Data.TaggedError("DocumentGenerating")<{
  readonly documentId: DocumentId;
}> {}

// An operation expected the draft to be in "submitted" status but it is not.
export class DraftNotSubmitted extends Data.TaggedError("DraftNotSubmitted")<{
  readonly draftId: DraftId;
  readonly actual: DraftStatus;
}> {}

// An operation expected the draft to be in "open" status but it is not.
export class DraftNotInOpenStatus extends Data.TaggedError("DraftNotInOpenStatus")<{
  readonly draftId: DraftId;
  readonly actual: DraftStatus;
}> {}

// The draft is in a stale state (submitted then rebased away); the owner must re-draft.
export class DraftStale extends Data.TaggedError("DraftStale")<{
  readonly draftId: DraftId;
}> {}

// A land or gate decision hit a rebase conflict; the conflicting regions are returned.
export class DraftConflict extends Data.TaggedError("DraftConflict")<{
  readonly draftId: DraftId;
  readonly regions: readonly OffsetRegion[];
}> {}

// `DraftStore.catchUpAndSubmit`'s fold (a long-stale `open` draft catching up to head at the
// open→submitted boundary, `catch-up-draft.ts`) hit a landed commit that conflicts with the draft's
// own changes. Unlike a submitted draft's rebase conflict (which retires to `stale`,
// `rebaseDraftOverLanded`), the draft here is still `open`, so the fold's own conflict-with-regions
// branch applies (changes remapped, `conflictRegions` populated, status stays `open`) — but the
// submit itself is refused rather than silently landing a lossy auto-merge the author never saw.
// The draft is persisted exactly as the fold left it (open, caught up, regions visible) so the
// editor shows the conflict; the author resolves it and resubmits.
export class DraftCatchUpConflict extends Data.TaggedError("DraftCatchUpConflict")<{
  readonly draftId: DraftId;
  readonly regions: readonly OffsetRegion[];
}> {}

// The per-document open-draft cap would be exceeded by creating a new draft.
export class DraftLimitExceeded extends Data.TaggedError("DraftLimitExceeded")<{
  readonly limit: number;
}> {}

// The agent's MCP write surface is scoped to curriculum documents (ADR-0053): a `draft-open` or
// `annotate` naming a `resources/*` target (the rubric/glossary) is refused. Experts keep editing
// resources on the web draft surface (plan 006); the agent only ever READS them — as read-only
// session file mounts, never as an MCP write target. Terminal, not recoverable: no retarget within
// the same document can fix it, so the agent must stop rather than retry.
// The chosen version's text is byte-identical to the current head — restoring it would append a
// no-op version whose diff is empty, which is noise in the timeline rather than a record of anything.
// Refused rather than silently ignored so the surface can say why nothing happened.
export class NothingToRestore extends Data.TaggedError("NothingToRestore")<{
  readonly documentId: DocumentId;
  readonly atVersion: number;
}> {
  override get message(): string {
    return `version ${this.atVersion} of ${this.documentId} is identical to the current head — nothing to restore`;
  }
}

// A restore lands through the HUMAN land path (`landHumanDraft`), so only a human viewer may ask for
// one. Defence in depth, mirroring `LandRequiresHumanSender`: the agent has no socket in production,
// and refusing here means a rejected restore never mints the draft it would otherwise abandon.
export class RestoreRequiresHumanSender extends Data.TaggedError("RestoreRequiresHumanSender")<{
  readonly documentId: DocumentId;
}> {
  override get message(): string {
    return `${this.documentId}: only a human viewer can restore a version`;
  }
}

export class ResourceNotAgentEditable extends Data.TaggedError("ResourceNotAgentEditable")<{
  readonly documentId: string;
}> {
  override get message(): string {
    return `${this.documentId} is not agent-editable: the agent's draft/annotate surface is scoped to curricula/ documents; the rubric and glossary are read-only static files (ADR-0055), not editable documents`;
  }
}

// A ChangeSetJson payload failed schema decoding.
export class MalformedChangeSet extends Data.TaggedError("MalformedChangeSet")<{
  // The underlying schema parse error.
  readonly cause: unknown;
}> {}

// A gate decision (accept/reject) was already made on a draft — first decision wins.
// Extends the `AlreadyDecided` concept (pending.ts) to cover draft decisions (U8).
export class AlreadyDecidedOnDraft extends Data.TaggedError("AlreadyDecidedOnDraft")<{
  readonly draftId: DraftId;
  // Who decided first.
  readonly decidedBy: string;
  // The outcome of the prior decision.
  readonly outcome: "accepted" | "rejected";
}> {}

// The actor is not the draft's owner; they may not mutate draft content (update/submit/land).
// Cross-owner discard is allowed (D11) — this error only guards content-mutating ops.
export class NotDraftOwner extends Data.TaggedError("NotDraftOwner")<{
  readonly draftId: DraftId;
  readonly ownerConnectionId: string;
  readonly actorConnectionId: string;
}> {}

// A draft is in a terminal (landed or discarded) state and cannot be mutated.
export class DraftAlreadyTerminal extends Data.TaggedError("DraftAlreadyTerminal")<{
  readonly draftId: DraftId;
  readonly status: DraftStatus;
}> {}

// A submit (`draft-submit` / `draft-complete`) arrived with an empty or whitespace-only change
// description. A submitted draft MUST carry a non-empty description so the gate-2 reviewer and the
// audit log always have the author's one-line explanation of the change — the submit is refused
// rather than landing a description-less draft (the agent path folds this to `OpError`).
export class DraftDescriptionRequired extends Data.TaggedError("DraftDescriptionRequired")<{
  readonly draftId: DraftId;
}> {}

// ─── WS authorization errors (U5) ──────────────────────────────────────────────
//
// The three gate-2 / discard authorization refusals that are NOT plain ownership checks
// (`NotDraftOwner` already covers "sender must own the draft"). These guard the sender's *kind*
// (human vs. agent) and the draft's *ownership kind*, independent of who specifically sent the frame.

// `agent-draft-decision` (gate 2) requires a human sender — the agent may not gate its own draft.
export class GateRequiresHumanSender extends Data.TaggedError("GateRequiresHumanSender")<{
  readonly draftId: DraftId;
}> {}

// `agent-draft-decision` only applies to an agent-owned draft; a human-owned draft lands via
// `land-request` instead.
export class DraftNotAgentOwned extends Data.TaggedError("DraftNotAgentOwned")<{
  readonly draftId: DraftId;
}> {}

// A cross-owner `draft-discard` (D11) is allowed to any human sender, but never to the agent
// identity discarding a draft it does not own — the agent may only discard its own draft.
export class DiscardRequiresHuman extends Data.TaggedError("DiscardRequiresHuman")<{
  readonly draftId: DraftId;
  readonly actorConnectionId: string;
}> {}

// A `land-request` against an agent-owned draft — agent drafts land ONLY through gate 2's
// `agent-draft-decision` (a human accepts), never a direct land, even by the agent owner itself.
export class AgentDraftRequiresGate extends Data.TaggedError("AgentDraftRequiresGate")<{
  readonly draftId: DraftId;
}> {}

// `land-request` requires a human sender (U13 Part B, ADR-0044 extension): any human may confirm
// a HUMAN-owned, submitted draft, but the agent has no socket in production (U7) and never issues
// a `land-request` of its own — this guard is defence-in-depth for the transport-independent
// `dispatchClientMessage`/`landHumanDraft` entry point tests exercise directly.
export class LandRequiresHumanSender extends Data.TaggedError("LandRequiresHumanSender")<{
  readonly draftId: DraftId;
}> {}

// ─── Agent collab-peer errors (U7) ──────────────────────────────────────────────
//
// The headless agent peer holds one open draft per session and serves the agent a
// windowed view of its draft text before each edit. These three errors model the
// session-shaped failures the peer surfaces to the agent loop as tagged tool results.

// A draft op (edit/diff/complete) ran while the session had no open draft — the agent must
// `draft-open` first.
export class NoOpenDraft extends Data.TaggedError("NoOpenDraft")<{
  readonly documentId: DocumentId;
}> {}

// `draft-open` ran while the session already had an open draft — one draft per session.
export class AgentDraftAlreadyOpen extends Data.TaggedError("AgentDraftAlreadyOpen")<{
  readonly draftId: DraftId;
}> {}

// A `draft-edit` addressed coordinates against text the agent last SAW (invariant 1), but the
// draft advanced since it was served (a rebase over an intervening land forwarded the draft's
// changes). The served coordinates no longer address the same text, so the edit is refused rather
// than silently applied to shifted text — the agent must re-read (or `draft-diff`) and retry.
//
// Historical: the offset-basis `draft-edit` raised this. The locator-based `draft-edit` (ADR-0048)
// resolves a re-locatable anchor against the draft buffer, so it is never stale — this error is no
// longer produced. The class is retained as vocabulary for the older event/error record.
export class StaleEditCoordinates extends Data.TaggedError("StaleEditCoordinates")<{
  readonly draftId: DraftId;
  readonly servedDraftVersion: number;
  readonly currentDraftVersion: number;
}> {}

// `draft-edit`'s non-leaf safety guard (KTD3a, block-locator refactor plan U4): a block's extent is
// its whole subtree, so a whole-block replace of a non-leaf (a unit/cycle/module with children)
// re-emits every descendant too. The block hash CAS proves the agent read the CURRENT block; it does
// NOT prove `newContents` preserved every untouched descendant heading, so an omitted one would
// otherwise be silently lost. `dropped` lists the missing descendant heading title(s) — each may
// legitimately recur (e.g. every module's `##### Authored Exercises`), so a title is flagged only
// when `newContents` carries fewer occurrences of it than the current block does. A deliberate
// descendant RENAME is rare and goes through a per-child edit instead of a whole-block replace.
export class BlockChildrenDropped extends Data.TaggedError("BlockChildrenDropped")<{
  readonly draftId: DraftId;
  readonly dropped: readonly string[];
}> {}

// `draft-edit`'s root-deletion guard: `newContents.length === 0` is the empty-replace-as-delete
// convention (`buildBlockEditChangeSet`), and deleting the depth-1 curriculum root would delete the
// document's ENTIRE text (the root's span IS the whole document) — there is no smaller edit that
// "deletes the curriculum" the agent could plausibly intend via this op. Refused before anything
// folds; the agent should delete or restructure the root's CHILD blocks instead (each child's own
// `draft-edit(child, "")` is the well-formed way to remove structure).
export class RootDeletionForbidden extends Data.TaggedError("RootDeletionForbidden")<{
  readonly draftId: DraftId;
}> {}

// `draft-replace`'s structural-loss guard: `resolveSpanLocator` resolves `locator.text` WITHIN the
// addressed block only (R-E), with no awareness of the block's own child headings — a `find`/`replace`
// whose resolved span happens to CROSS a descendant heading line would fold straight over it, silently
// deleting or mangling that heading (the surgical span-replace has no descendant-loss guard of its own,
// unlike `draft-edit`'s whole-block replace, which re-emits — and so can be checked against —
// `newContents` in full). Refused before anything folds; the agent should use `draft-edit` for a change
// that legitimately restructures a child heading, or narrow `locator.text` to stay within the block's
// own content (before its first child heading). Deliberately NOT applied to `resolveSpanLocator` itself:
// that resolver also serves `annotate`/`write-annotation`, and a finding/annotation span crossing a
// heading is non-destructive (it anchors a read, it never folds a change) and must remain allowed.
export class SpanCrossesHeading extends Data.TaggedError("SpanCrossesHeading")<{
  readonly draftId: DraftId;
}> {}

// ─── Update-log projection ─────────────────────────────────────────────────────
//
// The update-log projection extracts the ChangeSetJson from every patch-bearing
// audit event in order, indexed by the collabVersion they produced. This is the
// pure function that derives the collab update feed from the audit log — no IO,
// no stored artifact.

// One entry in the update-log projection — the ChangeSet at a particular version.
export interface UpdateLogEntry {
  readonly version: number;
  readonly changes: ChangeSetJson;
}

// The full, ordered projection of patch-bearing audit events from the collab-era
// audit log. Events are ordered by their collabVersion ascending.
export type UpdateLog = readonly UpdateLogEntry[];

// The shape of a patch-bearing audit event as this module understands it.
// Collab-era events carry `changeSetJson` + `newVersion`; legacy DMP events carry
// `patch`/`docVersion` and are NOT included in the update-log projection.
export interface PatchBearingEvent {
  readonly _tag: string;
  readonly changeSetJson?: unknown;
  readonly newVersion?: unknown;
}

// Projects a sequence of audit events into an ordered UpdateLog by extracting
// only the patch-bearing collab events (those carrying `changeSetJson` +
// `newVersion`). DMP/legacy events are excluded. The result is indexed by version,
// ascending.
//
// Returns Either.right(UpdateLog) on success or Either.left(MalformedChangeSet) on the
// FIRST event whose `changeSetJson` fails decode or whose `newVersion` is not a valid
// non-negative integer. Failing loudly on a malformed patch-bearing event is intentional:
// a silently dropped update creates a version hole in the collab feed that clients can
// never bridge (position mapping over the gap produces garbage). When in doubt, refuse
// and surface — never guess (plan policy).
//
// Events that simply lack both `changeSetJson` AND `newVersion` are not patch-bearing
// and are silently filtered out — that is the normal non-patch event path, not an error.
//
// This is the pure contract that U3 implements more fully with real ChangeSet
// rebasing. Here it defines the type and the working extraction logic.
export const projectUpdateLog = (
  events: readonly PatchBearingEvent[],
): Either.Either<UpdateLog, MalformedChangeSet> => {
  const entries: UpdateLogEntry[] = [];
  for (const event of events) {
    // Events with neither field are non-patch-bearing — skip silently.
    if (event.changeSetJson === undefined || event.newVersion === undefined) continue;
    // A patch-bearing event (has at least one of the fields) with an invalid newVersion is
    // malformed — refuse loudly so the caller surfaces the version hole.
    const version = event.newVersion;
    if (typeof version !== "number" || !Number.isInteger(version) || version < 0) {
      return Either.left(
        new MalformedChangeSet({
          cause: `event _tag=${event._tag} has invalid newVersion: ${String(version)}`,
        }),
      );
    }
    const decoded = decodeChangeSetJson(event.changeSetJson);
    if (Either.isLeft(decoded)) {
      return Either.left(
        new MalformedChangeSet({
          cause: `event _tag=${event._tag} at version ${version} has malformed changeSetJson: ${String(decoded.left.cause)}`,
        }),
      );
    }
    entries.push({ version, changes: decoded.right });
  }
  // Sort by version ascending to ensure a consistent ordering even if the event
  // slice arrived unordered (e.g. from a chunked pull).
  entries.sort((a, b) => a.version - b.version);
  return Either.right(entries);
};

// ─── Wire payload schemas (members of ClientMessage / ServerMessage, U5) ──────
//
// Per the coupled-landing constraint in the plan: U2 defined these payload schemas here; U5 joined
// them to the wire unions in sync.ts (ClientMessage/ServerMessage) together with ws.ts's dispatch
// and broadcast.ts's stream handling.

// ── Client → Server payloads ──────────────────────────────────────────────────

// Open a new draft forked from the current head.
export const DraftOpenPayload = Schema.TaggedStruct("draft-open", {
  docId: DocumentId,
  reason: Schema.optional(Schema.String),
});
export type DraftOpenPayload = typeof DraftOpenPayload.Type;

// Push a keystroke-batched ChangeSet delta to the server.
export const DraftUpdatePayload = Schema.TaggedStruct("draft-update", {
  draftId: DraftId,
  // The delta since the last acknowledged update.
  delta: ChangeSetJson,
  // The owner's monotonic draft version counter (for optimistic concurrency).
  expectedDraftVersion: Schema.Int.pipe(Schema.nonNegative()),
});
export type DraftUpdatePayload = typeof DraftUpdatePayload.Type;

// The owner declares their draft ready for landing (human) or review (agent → gate 2). `description`
// is the mandatory one-line change explanation (enforced non-empty server-side at the submit
// boundary); the human submit UI collects it before enabling Submit.
export const DraftSubmitPayload = Schema.TaggedStruct("draft-submit", {
  draftId: DraftId,
  description: Schema.String,
  // The finding-id(s) this draft answers (ADR-0049 D3). Human self-lands name none (empty), so this
  // defaults to []; the agent chat-turn skill attaches the annotation(s) each draft resolves.
  resolves: Schema.optionalWith(Schema.Array(AnnotationId), { default: () => [] }),
});
export type DraftSubmitPayload = typeof DraftSubmitPayload.Type;

// The owner (or any human, per D11) discards a draft.
export const DraftDiscardPayload = Schema.TaggedStruct("draft-discard", {
  draftId: DraftId,
  reason: Schema.optional(Schema.String),
});
export type DraftDiscardPayload = typeof DraftDiscardPayload.Type;

// A human submits a land request for their own draft.
export const LandRequestPayload = Schema.TaggedStruct("land-request", {
  draftId: DraftId,
});
export type LandRequestPayload = typeof LandRequestPayload.Type;

// Roll a document forward onto the content of one of its own earlier versions (ADR-0112). NOT a
// history rewrite and NOT a branch: the server reconstructs the text at `atVersion`, diffs it against
// the CURRENT head, and puts that diff through the ordinary draft → submit → land path, so the
// restored content arrives as a NEW version appended on top of everything that came after it. Every
// intermediate version stays in the log and stays reachable; a restore is itself restorable.
//
// Root-agnostic by construction (it addresses a rooted `DocumentId`): a reference guide and a
// language pack get this exactly as a curriculum does, which is the point of ADR-0112. Contrast
// `cloneAtVersion` (`document-create.ts`), the other version-recovery affordance: that mints a new,
// independent CURRICULUM seeded from the chosen version and leaves the source untouched. Clone forks;
// restore advances.
export const RestoreVersionPayload = Schema.TaggedStruct("restore-version", {
  docId: DocumentId,
  atVersion: CollabVersion,
});
export type RestoreVersionPayload = typeof RestoreVersionPayload.Type;

// A human accepts or rejects a submitted agent draft (gate 2).
export const AgentDraftDecisionPayload = Schema.TaggedStruct("agent-draft-decision", {
  draftId: DraftId,
  decision: Schema.Literal("accept", "reject"),
  reason: Schema.optional(Schema.String),
  // Findings the reviewer named as answered by this draft, on top of whatever the draft already
  // carries in its own `resolves` (ADR-0049 D3, U7/R8) — the server unions the two at the land
  // seam. Same optional-with-default shape as `DraftSubmitPayload.resolves`, so an older client's
  // decision frame decodes to `[]` unchanged. Ignored on the `reject` branch.
  resolves: Schema.optionalWith(Schema.Array(AnnotationId), { default: () => [] }),
});
export type AgentDraftDecisionPayload = typeof AgentDraftDecisionPayload.Type;

// ── Server → Client payloads ──────────────────────────────────────────────────

// The receiveUpdates feed: a ChangeSet landed at a new version.
export const CommitLandedPayload = Schema.TaggedStruct("commit-landed", {
  docId: DocumentId,
  changes: ChangeSetJson,
  newVersion: CollabVersion,
  // Attribution — who authored this commit.
  owner: EditorIdentity,
});
export type CommitLandedPayload = typeof CommitLandedPayload.Type;

// A draft snapshot/update broadcast — every connected viewer receives this for every
// active draft change (or on bootstrap for the full open-drafts set).
export const DraftStatePayload = Schema.TaggedStruct("draft-state", {
  draft: Draft,
  // The server's wall time when this frame was produced (U7). Paired with `draft.lastActivityAt`,
  // it lets the client derive a clock-skew-immune idle age: `serverNow - lastActivityAt` is a
  // server-to-server duration, so a skewed client clock never enters the "is this draft idle?" test.
  serverNow: Schema.DateTimeUtc,
});
export type DraftStatePayload = typeof DraftStatePayload.Type;

// The diagnostics push: open annotations projected to head offsets, version-keyed.
export const DiagnosticAnchor = Schema.Struct({
  annotationId: AnnotationId,
  rule: Schema.String,
  rationale: Schema.String,
  from: Schema.Int.pipe(Schema.nonNegative()),
  to: Schema.Int.pipe(Schema.nonNegative()),
  // True if the annotated text was deleted — zero-length gutter-only marker.
  collapsed: Schema.optional(Schema.Boolean),
  // CON-737 (U6): the canonical severity vocabulary (`packages/core/src/rubric-catalog.ts`'s
  // `RulePriority`) — `rulePriority` was built for exactly this grouping, so it wins over the CM-
  // shaped `error|warning|info` this field used to carry (still translated to CM's own `Severity`
  // at the editor boundary, `apps/web/src/diagnostics-client.ts`, which is a fixed third-party
  // vocabulary this field no longer needs to match). Derived for a judge finding (from the rubric
  // catalog) and for a lint/audit finding (`fatal → blocker`, `warning → warning`); stamped only for
  // a manual finding, which has no catalog entry to derive from.
  severity: FindingSeverity,
  message: Schema.String,
  // CON-737 (U6): which rail produced this finding — a different axis from `author` below (`author`
  // says WHO wrote it — a person or a role; `kind` says WHICH RAIL produced it). Derived from
  // `author` (`expert → manual`, `unit-judge | cross-unit-judge → judge`) but carried as its own
  // field rather than re-derived downstream, since `findingSummary` groups by kind and every
  // consumer should read the field its own comment names, not re-run the `author === "expert"` test.
  kind: Schema.Literal("judge", "manual"),
  // The finding's author (ADR-0049), carried so the reviewer UI can label a manual (`expert`)
  // finding distinctly from a judge finding (a judge finding's ignored fingerprint suppresses
  // re-flags; a manual finding's Ignore is permanent — ADR-0089 — but both ignore in one click).
  // The literal is inlined rather than imported from `audit.ts` to keep this leaf module free of an
  // import cycle (`audit.ts` already depends on `collab.ts`). Optional so a legacy/pre-ADR-0049
  // diagnostic decodes.
  author: Schema.optional(Schema.Literal("expert", "unit-judge", "cross-unit-judge")),
  // The PERSON behind an `expert` finding, as a display NAME — never the `ViewerId` that produced it
  // (ADR-0013: server-side identity does not go on a wire). `projectDiagnostics` derives it from the
  // `annotation.created.v2` event's `createdBy` through core's own `deriveDisplayName`, exactly as
  // the `read-findings` op derives its own `authorName` at its boundary. Absent for every judge and
  // agent finding (they carry a role, not a person) and for legacy manual findings written before
  // `createdBy` existed — so a consumer labels those from `author` alone.
  authorName: Schema.optional(Schema.String),
});
export type DiagnosticAnchor = typeof DiagnosticAnchor.Type;

export const DiagnosticsPayload = Schema.TaggedStruct("diagnostics", {
  docId: DocumentId,
  diagnostics: Schema.Array(DiagnosticAnchor),
  atVersion: CollabVersion,
  // Dialect breaches from `CurriculumChecker` (`RailFinding`, U11 — core's `Finding` widened with an
  // optional `instructText`; a lint rule never carries `instruct`, so this field's `instructText` is
  // always absent in practice), derived fresh from the head text on every recompute — NOT audit
  // events, since an event log of them would need retiring the moment the text is fixed. Keyed to the
  // same `atVersion` as `diagnostics`. Optional/defaulted so a frame predating this field still decodes.
  lint: Schema.optionalWith(Schema.Array(RailFinding), { default: () => [] }),
  // Exercise-content findings from `CurriculumChecker.check` (CON-744, plan W1) — the SAME
  // `RailFinding` shape as `lint`, from a different registry (`AUDIT_RULES`), derived from the same
  // head text at the same `atVersion`. A separate field rather than more entries in `lint` because
  // provenance is the one thing the merged array cannot recover: both families squiggle in the editor
  // and both reach the rail, but each is labelled with the registry it came from, and re-deriving that
  // downstream would mean re-testing `ruleId` against `AUDIT_RULES` to recover what the producer
  // already knew. Optional/defaulted on the same reasoning as `lint`. `instructText` (U11, CON-1315
  // Decision 14) is populated here — via `diagnostics.ts`'s `computeDiagnostics`, mirroring
  // `run-checks.ts`'s `withResolution` — for a finding whose audit rule carries an `instruct`: the one
  // deliberate, narrow case where remediation text now reaches the rail, not just an agent.
  audit: Schema.optionalWith(Schema.Array(RailFinding), { default: () => [] }),
  // CON-737 (U6): the document's quality readout — open-finding counts per severity bucket, across
  // judge, manual and lint/audit findings alike (`packages/core/src/finding-summary.ts`). Replaces
  // the deleted weighted-mean rubric score. Computed fresh every recompute from `diagnostics`/`lint`/
  // `audit` above, never stored — so, unlike them, it needs no optional/defaulted legacy-decode
  // allowance: every frame this schema encodes carries one.
  findingSummary: FindingSummary,
  // U7 (CON-739): whether the check suite has produced a verdict for this document at all — see
  // `judgeChecksRan` for how it is derived (a partId bucket exists in the store). A judge count of `0`
  // is ambiguous: it means "the last run found nothing" OR "no run has EVER covered this document" —
  // the store persists across restarts (U2, CON-955), so the second case is now rare rather than the
  // steady state after every restart, but it still occurs for a document no run has ever touched. A
  // reviewer must not read it as an all-clear the suite never gave. Optional/defaulted on the same
  // legacy-decode reasoning as `lint`,
  // and `false` is the right default: a frame that cannot say has not seen a verdict.
  checksRan: Schema.optionalWith(Schema.Boolean, { default: () => false }),
  // ADR-0095 / CON-893: whether `audit` above is a real recompute or a fallback empty list. Part of
  // the registry now runs behind a Pyodide call (`apps/server/src/adapters/pyodide-authored-audit.ts`),
  // which can time out or crash — the same `checksRan` reasoning applies: `audit: []` is ambiguous
  // between "checked, found nothing" and "the WASM call failed, this frame has no opinion", and a
  // reviewer must not read the second as an all-clear the suite never gave. `true` is the right
  // default (unlike `checksRan`'s `false`): every frame predating this field came from a wholly
  // synchronous, always-succeeding `audit`, so defaulting a legacy frame to "ran" is honest, not
  // optimistic.
  auditRan: Schema.optionalWith(Schema.Boolean, { default: () => true }),
  // CON-1314: whether this document is ready to enrich, plus the two inputs that decided it.
  // Server-computed because only the server can run the conversion, and splitting the two halves
  // would let the panel show a verdict assembled from two different moments. Optional/defaulted on
  // the same legacy-decode reasoning as `lint`; the default is the conservative one, because a
  // frame that cannot say must not read as an all-clear.
  enrichmentReadiness: Schema.optionalWith(EnrichmentReadinessSchema, {
    default: () => READINESS_NOT_COMPUTED,
  }),
  // The conversion's own findings, kept out of `lint`/`audit` because their rule ids are not in
  // those registries (see `ConversionFindingSchema`). They were computed, counted into readiness and
  // then dropped, so a document that failed to convert showed "1 critical finding" with no rule, no
  // message and no line — the anchor CON-1314 asks for, computed and discarded.
  conversion: Schema.optionalWith(Schema.Array(ConversionFindingSchema), { default: () => [] }),
});
export type DiagnosticsPayload = typeof DiagnosticsPayload.Type;

// A private, per-connection push (never broadcast) telling the connecting viewer which open/submitted
// draft(s) on this document are their OWN — keyed by the stable `ViewerId` the WS upgrade resolved,
// never carried on the frame itself (ADR-0050's "ViewerId never on the wire" invariant: this payload
// names drafts by `DraftId`, not by identity). Sent once per connection (alongside the bootstrap
// snapshot) so the client can render a "My drafts" surface and resume editing after a reload/reconnect
// — see `DraftStore.findOpenForViewer`. At most one entry today (`findOpenForViewer`'s "one open draft
// at a time" invariant), but the shape is a list so a future relaxation of that cap needs no wire change.
export const MyDraftsPayload = Schema.TaggedStruct("my-drafts", {
  docId: DocumentId,
  draftIds: Schema.Array(DraftId),
});
export type MyDraftsPayload = typeof MyDraftsPayload.Type;

// Extended Snapshot fields (added to Snapshot in U5 as part of the bootstrap peel).
// Exported here so `sync.ts` can compose them in.
//
// `lint`/`audit`/`findingSummary`/`checksRan`/`auditRan` were removed from this struct (WS
// first-message latency fix, ADR-0045's dated amendment): computing them was blocking the bootstrap
// Snapshot behind the same `projectDiagnostics`/`Curriculum.lint`/`Curriculum.audit` cost
// `DiagnosticsService` pays on every live edit. They now arrive exclusively on the
// `DiagnosticsPayload` ("diagnostics") frame `documentStream` forwards once `DiagnosticsService`
// finishes computing it — the two schemas otherwise carry the exact same fields, so nothing
// downstream needed a new wire shape.
export const SnapshotCollabFields = Schema.Struct({
  // The collab version of the head (same as docVersion in the audit fold).
  collabVersion: CollabVersion,
  // Full open-draft states — late joiners can render every draft diff immediately.
  openDrafts: Schema.Array(Draft),
  // The server's wall time when this snapshot was produced (U7). Same role as `DraftStatePayload`'s
  // `serverNow`: the seed path computes each open draft's idle age server-relative to THIS stamp, so
  // a late joiner's idle-hide predicate is clock-skew-immune from the first frame.
  serverNow: Schema.DateTimeUtc,
  // Kept, unlike its five siblings above — deliberately, and only for one release. Every sibling
  // was already `Schema.optionalWith(..., {default})` before ADR-0045's amendment, so their wire key
  // was already optional and an old client decoding a new server's Snapshot tolerates their absence
  // exactly as it always could. `diagnostics` was NOT: it was a bare `Schema.Array(DiagnosticAnchor)`,
  // required on the wire. Dropping it outright would mean a rolling deploy's still-old client bundle
  // (served from nginx as a static SPA, independently deployed from the server) fails to decode ANY
  // Snapshot from an already-new server — not a degraded diagnostics view, a hard decode failure on
  // the bootstrap frame. `optionalWith` with a default makes the encoded key optional (old clients on
  // an old server still see it every time) while the new server keeps emitting `diagnostics: []` (the
  // constructor default) so a new-server, old-client pairing still decodes. Delete this field outright
  // once the fleet has fully rolled past the release that introduced this amendment.
  diagnostics: Schema.optionalWith(Schema.Array(DiagnosticAnchor), { default: () => [] }),
});
export type SnapshotCollabFields = typeof SnapshotCollabFields.Type;

// ─── Pure rebase over the landed-commit history ───────────────────────────────
//
// `rebase` maps a draft's composite ChangeSet over every commit that landed since
// the draft was forked, in order. For each landed commit, it checks for overlapping
// changed ranges (conflict detection); if the draft and a landed commit both touch
// the same region, it returns `Either.left(DraftConflict)` — the seam never lands a
// guessed merge (plan D4 / "No guessed merges").
//
// Conflict detection strategy: compose all landed commits so far, map the draft over
// the composed landed, then use `iterChangedRanges` on both the original draft and
// each landed set to find overlapping pre-land ranges. Specifically, for each step we
// compare the current (accumulated) draft's changed ranges against the current landed
// commit's changed ranges in pre-step coordinates. Two ranges [a, b) and [c, d) overlap
// when a < d && c < b. Conflicting region coordinates are post-land (in the document
// produced after the landed commit applied).
//
// Why stepwise mapping (not a single compose-all-then-map):
// - We must report conflicts at the step where they arise (the landed commit that caused
//   it), and the post-land coordinates of the conflict region are relative to the doc
//   after THAT step's landed commit, not the final doc.
// - Stepwise also lets us stop at the first conflict, matching the "first accept wins"
//   policy (D4).
//
// `draftId` is passed in because `DraftConflict` requires it — callers always have it.

export const rebase = (
  draftChanges: ChangeSetJson,
  landedSince: readonly ChangeSetJson[],
  draftId: DraftId,
): Either.Either<ChangeSetJson, DraftConflict> => {
  let current = ChangeSet.fromJSON(draftChanges);
  for (const landedJson of landedSince) {
    const landed = ChangeSet.fromJSON(landedJson);
    // Collect the changed ranges of the current (accumulated) draft in pre-step coordinates.
    const draftRanges: Array<{ from: number; to: number }> = [];
    current.iterChangedRanges((fromA, toA) => {
      draftRanges.push({ from: fromA, to: toA });
    });
    // Collect the changed ranges of this landed commit in pre-step coordinates.
    const landedRangesB: Array<{ fromA: number; toA: number; fromB: number; toB: number }> = [];
    landed.iterChangedRanges((fromA, toA, fromB, toB) => {
      landedRangesB.push({ fromA, toA, fromB, toB });
    });
    // Check for overlaps between draft ranges (pre-step) and landed ranges (pre-step).
    // Two ranges [a,b) and [c,d) overlap when a < d && c < b.
    const conflictRegions: OffsetRegion[] = [];
    for (const d of draftRanges) {
      for (const l of landedRangesB) {
        if (d.from < l.toA && l.fromA < d.to) {
          // Conflict: record the post-land region (fromB, toB are post-land coordinates).
          conflictRegions.push({ from: l.fromB, to: l.toB });
        }
      }
    }
    if (conflictRegions.length > 0) {
      return Either.left(new DraftConflict({ draftId, regions: conflictRegions }));
    }
    // No conflict — map the current draft over this landed commit.
    current = current.map(landed);
  }
  return Either.right(ChangeSetJson.make(current.toJSON()));
};

// ─── Apply a ChangeSetJson to a string ───────────────────────────────────────
//
// The seam and the reconstruction fold both need this helper. It converts the string
// to a CM Text (splitting on newlines), applies the ChangeSet, and converts back.
// Used by doc-history.ts for the collab leg of the hybrid reconstruction fold.

export const applyChangeSetJson = (text: string, changes: ChangeSetJson): string => {
  const doc = Text.of(text.split("\n"));
  return ChangeSet.fromJSON(changes).apply(doc).toString();
};

// ─── Compose two ChangeSetJson in sequence ───────────────────────────────────
//
// `second` must be expressed over the document `first` produces (CM's own precondition:
// `second.length === first.newLength`). Used by the write-boundary normalization (ADR-0079) to fold a
// formatter's delta into the edit delta it follows, so ONE ChangeSet carries both — the edit stays
// localized and replaying the composed set reproduces the persisted bytes exactly.

export const composeChangeSetJson = (first: ChangeSetJson, second: ChangeSetJson): ChangeSetJson =>
  ChangeSetJson.make(ChangeSet.fromJSON(first).compose(ChangeSet.fromJSON(second)).toJSON());
